import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  getCoachAthleteEvalHistory,
  getUserEvalHistory,
} from '@/services/evaluationApi';
import type {
  CoachAthleteEvalHistoryParams,
  UserEvalHistoryParams,
} from '@/services/evaluationApi';

/**
 * Hook to fetch eval-history roll-up for all athletes across the coach's
 * channels. Silently returns an empty array when the endpoint returns 404
 * (backend not yet deployed).
 */
export function useCoachAthleteEvalHistory(params?: CoachAthleteEvalHistoryParams) {
  return useQuery({
    queryKey: ['coach-athlete-eval-history', params],
    queryFn: async () => {
      try {
        return await getCoachAthleteEvalHistory(params);
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return [];
        }
        throw err;
      }
    },
  });
}

/**
 * Hook to fetch evaluation history for a specific athlete.
 * Returns null when the caller has no channel relationship (403)
 * or when the endpoint is not yet deployed (404).
 *
 * The `status` field distinguishes between these two cases:
 * - 'no-access' → 403 from backend
 * - 'not-found' → 404 (endpoint not deployed)
 * - 'ok' → successful fetch
 */
export type UserEvalHistoryStatus = 'loading' | 'ok' | 'no-access' | 'not-found' | 'error';

export function useUserEvalHistory(userId: string | undefined, params?: UserEvalHistoryParams) {
  return useQuery({
    queryKey: ['user-eval-history', userId, params],
    enabled: !!userId,
    queryFn: async () => {
      try {
        const rows = await getUserEvalHistory(userId!, params);
        return { rows, status: 'ok' as const };
      } catch (err) {
        if (isAxiosError(err)) {
          if (err.response?.status === 403) {
            return { rows: [], status: 'no-access' as const };
          }
          if (err.response?.status === 404) {
            return { rows: [], status: 'not-found' as const };
          }
        }
        throw err;
      }
    },
  });
}
